import React, { useState } from 'react'
import {
    Card,
    CardContent,
    IconButton,
    Typography,
    Box,
    Snackbar,
    Alert,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Divider,
    useMediaQuery,
    useTheme,
    Link
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GoogleSignIn from './GoogleSignIn'
import { ArrowRight, Mail } from 'lucide-react'
import { getBackendUserUrl } from '../../utils/utils'

const LoginCard = ({ onClose, onUserLogin }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        userType: [],
    })
    const [errors, setErrors] = useState({})
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    })

    const handleChange = (e) => {
        const { name, value } = e.target
        
        // Special handling for userType multi-select
        if (name === 'userType') {
            setFormData({
                ...formData,
                [name]: value // value will be an array for multi-select
            })
        } else {
            setFormData({
                ...formData,
                [name]: value
            })
        }
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            })
        }
    }

    const validateForm = () => {
        const newErrors = {}
        
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required'
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required'
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid'
        }
        if (formData.userType.length === 0) newErrors.userType = 'Please select at least one role'
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm()) return;
        
        setLoading(true);
        
        try {
            // Update this mapping to match the MenuItem values
            const userTypeMapping = {
                'Host': 'Host',
                'Vendor': 'Vendor',
                'Sponsor': 'Sponsor',
                'VenueOwner': 'Venue',
                'Other': 'Host' // Default to Host for "other"
            };
            
            // Map each selected userType to its backend equivalent
            const mappedUserTypes = formData.userType.map(type => userTypeMapping[type] || 'Host');
            
            // Prepare the user data for the backend
            const userData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                companyAffiliation: formData.companyName || '',
                type: mappedUserTypes // Now an array of mapped types
            };
            console.log("userData", userData)
            
            // Make the API call to create the user
            const backendUrl = getBackendUserUrl()
            const response = await fetch(backendUrl + '/user/waitlist', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(userData),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to join waitlist');
            }
            
            // Show success state
            setSuccess(true);
            
            setNotification({
                open: true,
                message: "You've been added to our waitlist! We'll be in touch soon.",
                severity: 'success'
            });
        } catch (error) {
            console.error('Error submitting form:', error);
            setNotification({
                open: true,
                message: error.message || "Something went wrong. Please try again.",
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }

    const handleLoginSuccess = (result) => {
        console.log('Login success result:', result)
        
        // If we have Google account info, pre-fill the form
        if (result.email) {
            setFormData({
                ...formData,
                email: result.email,
                firstName: result.givenName || formData.firstName,
                lastName: result.familyName || formData.lastName
            })
        }
        
        setNotification({
            open: true,
            message: 'Google account connected. Please complete your profile.',
            severity: 'success',
        })
    }

    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') {
            return
        }
        setNotification({ ...notification, open: false })
    }

    const renderSuccessState = () => (
        <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ 
                width: 60, 
                height: 60, 
                borderRadius: '50%', 
                backgroundColor: 'rgba(126, 34, 206, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
            }}>
                <Mail size={30} color="#7E22CE" />
            </Box>
            
            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                You're on the Waitlist!
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', maxWidth: '80%', mx: 'auto' }}>
                Thank you for joining the Happenings waitlist. We'll notify you as soon as we're ready to welcome you to our beta program.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                <Button 
                    variant="contained"
                    component="a"
                    href="https://cal.com/happenings"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ 
                        background: 'linear-gradient(90deg, #5637ee 60%, #6da7d0 100%)',
                        borderRadius: 2,
                        color: 'white',
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(90deg, #6da7d0 0%, #5637ee 100%)'
                        }
                    }}
                >
                    Schedule a Demo
                </Button>
                
                <Button 
                    variant="outlined"
                    onClick={onClose}
                    size="small"
                    sx={{ 
                        borderRadius: 2,
                        borderColor: '#5637ee',
                        color: '#5637ee',
                        px: 3,
                        '&:hover': {
                            borderColor: '#6da7d0',
                            backgroundColor: 'rgba(86, 55, 238, 0.04)'
                        }
                    }}
                >
                    Close
                </Button>
            </Box>
        </Box>
    )

    const renderForm = () => (
        <>
            <Typography variant="body2" sx={{py: 2, mb: 2, color: 'text.secondary' }}>
                Join our waitlist to get early access to Happenings - the all-in-one event planning platform.
            </Typography>
            
            {/* First and Last Name in the same row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        First name
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="First name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        disabled={loading}
                        variant="outlined"
                        InputProps={{
                            sx: {
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d0d0d0',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#222222',
                                    borderWidth: '1px',
                                },
                                height: '56px',
                            }
                        }}
                    />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Last name
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Last name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        disabled={loading}
                        variant="outlined"
                        InputProps={{
                            sx: {
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d0d0d0',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#222222',
                                    borderWidth: '1px',
                                },
                                height: '56px',
                            }
                        }}
                    />
                </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Work Email
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="mail@example.com"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={loading}
                        variant="outlined"
                        InputProps={{
                            sx: {
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d0d0d0',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#222222',
                                    borderWidth: '1px',
                                },
                                height: '56px',
                            }
                        }}
                    />
                </Box>
            
            <FormControl 
                fullWidth 
                margin="normal" 
                error={!!errors.userType} 
                sx={{ mb: 3, mt: 2 }} 
                disabled={loading}
            >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Your Role(s)
                </Typography>
                <Select
                    name="userType"
                    value={formData.userType}
                    onChange={handleChange}
                    error={!!errors.userType}
                    displayEmpty
                    multiple
                    renderValue={(selected) => {
                        if (selected.length === 0) {
                            return <em>Select your role(s)</em>;
                        }
                        
                        const roleLabels = {
                            'Host': 'Event Host/Planner',
                            'Vendor': 'Vendor/Service Provider',
                            'Sponsor': 'Event Partner',
                            'VenueOwner': 'Venue',
                            'Other': 'Other'
                        };
                        
                        return selected
                            .map(value => roleLabels[value] || value)
                            .filter(label => label)
                            .join(', ');
                    }}
                    sx={{
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d0d0d0',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#222222',
                            borderWidth: '1px',
                        },
                        minHeight: '56px',
                    }}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 224,
                                width: 250,
                            },
                        },
                    }}
                >
                    <MenuItem disabled value="">
                        <em>Select your role(s)</em>
                    </MenuItem>
                    <MenuItem value="Host">Event Host/Planner</MenuItem>
                    <MenuItem value="Vendor">Vendor/Service Provider</MenuItem>
                    <MenuItem value="Sponsor">Event Partner</MenuItem>
                    <MenuItem value="VenueOwner">Venue</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                </Select>
                {errors.userType && <FormHelperText>{errors.userType}</FormHelperText>}
            </FormControl>
            
            <Button 
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                endIcon={<ArrowRight size={16} />}
                size="medium"
                disabled={loading}
                sx={{ 
                    background: 'linear-gradient(90deg, #5637ee 60%, #6da7d0 100%)',
                    borderRadius: 2,
                    py: 1.5,
                    mt: 2,
                    color: 'white',
                    '&:hover': {
                        background: 'linear-gradient(90deg, #6da7d0 0%, #5637ee 100%)',
                    },
                    '&.Mui-disabled': {
                        background: 'rgba(86, 55, 238, 0.5)',
                    }
                }}
            >
                {loading ? 'Joining Waitlist...' : 'Join Waitlist'}
            </Button>
            
            <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                    or
                </Typography>
            </Divider> 
            <Typography variant="h6" color="text.secondary" fontSize="1.0rem" sx={{ justifyContent: 'center'}} >
                    Sign in with Google
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleSignIn onUserLogin={handleLoginSuccess} disabled={loading} />
            </Box>

            <Typography 
                variant="body2" 
                color="text.secondary" 
                align="center" 
                sx={{ mt: 3, fontSize: '0.875rem' }}
            >
                By continuing, you agree to our{' '}
                <Link 
                    href="/terms" 
                    sx={{ 
                        color: '#5637ee',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    Terms of Service
                </Link>
                {' '}&{' '}
                <Link 
                    href="/privacy" 
                    sx={{ 
                        color: '#5637ee',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    Privacy Policy
                </Link>
            </Typography>
        </>
    )

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1300,
                p: 2,
                boxSizing: 'border-box'
            }}
        >
            <Card
                sx={{
                    width: 400,
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'auto',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
            >
                <CardContent
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 3, sm: 4 }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2
                        }}
                    >
                        <Typography variant="h6" fontWeight={600}>
                            {success ? 'Success!' : 'Join Happenings Waitlist'}
                        </Typography>
                        <IconButton
                            sx={{
                                color: 'text.secondary',
                                padding: 0.5
                            }}
                            aria-label="close"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {success ? renderSuccessState() : renderForm()}
                </CardContent>
            </Card>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                    elevation={6}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default LoginCard
